import type { ReactNode } from "react"
import { randomId } from "@/v1-ui/utils"
import cn from "@/v1-ui/utils/utils.cn"
import type { IconProps } from "@/v1-ui/Icon"
import Button from "@/v1-ui/Button"
import type { ButtonProps } from "@/v1-ui/Button"
import { useNotifications } from "./NotificationProvider"
import type { NotificationOptions } from "./NotificationProvider"

export type ConfirmationProps = {
  onConfirm(): void,
  buttonConfirmContent?: ReactNode,
  buttonConfirmClassName?: ButtonProps["className"],
  buttonConfirmStyle?: ButtonProps["style"],
  buttonConfirmIntent?: ButtonProps["intent"],
  buttonConfirmIconSrc?: ButtonProps["iconSrc"],
  buttonConfirmIconSize?: ButtonProps["iconSize"],
  buttonConfirmAriaLabel?: ButtonProps["aria-label"],
  onDeny?(): void,
  buttonDenyContent?: ReactNode
  buttonDenyClassName?: ButtonProps["className"],
  buttonDenyStyle?: ButtonProps["style"],
  buttonDenyIntent?: ButtonProps["intent"],
  buttonDenyIconSrc?: ButtonProps["iconSrc"],
  buttonDenyIconSize?: ButtonProps["iconSize"],
  children?: ReactNode
}

function Confirmation(props: ConfirmationProps) {
  const {
    onConfirm,
    buttonConfirmClassName,
    buttonConfirmContent,
    buttonConfirmIconSize,
    buttonConfirmIconSrc,
    buttonConfirmIntent,
    buttonConfirmStyle,
    buttonConfirmAriaLabel,
    onDeny,
    buttonDenyClassName,
    buttonDenyContent,
    buttonDenyIconSize,
    buttonDenyIconSrc,
    buttonDenyIntent,
    buttonDenyStyle,
    children
  } = props

  return (
    <>
      {children}

      <div className="confirmation-btns">
        <Button
          className={cn("confirmation-deny-btn", buttonDenyClassName)}
          style={buttonDenyStyle}
          intent={buttonDenyIntent}
          iconSrc={buttonDenyIconSrc}
          iconSize={buttonDenyIconSize}
          onClick={onDeny}
        >
          {buttonDenyContent}
        </Button>
        <Button
          autoFocus
          className={cn("confirmation-confirm-btn", buttonConfirmClassName)}
          aria-label={buttonConfirmAriaLabel}
          style={buttonConfirmStyle}
          intent={buttonConfirmIntent}
          iconSrc={buttonConfirmIconSrc}
          iconSize={buttonConfirmIconSize}
          onClick={onConfirm}
        >
          {buttonConfirmContent}
        </Button>
      </div>
    </>
  )
}

export default Confirmation

export type ConfirmationOptions = NotificationOptions & ConfirmationProps & {
  isAutoCloseEnabled?: boolean
}

export function useConfirmation() {
  const { addNotification, clearNotification } = useNotifications()
  return {
    addConfirmation(props: ConfirmationOptions) {
      const {
        onConfirm,
        buttonConfirmClassName,
        buttonConfirmContent = "Ja, jeg er sikker",
        buttonConfirmIconSize,
        buttonConfirmIconSrc,
        buttonConfirmIntent = "danger",
        buttonConfirmStyle,
        buttonConfirmAriaLabel,
        onDeny,
        buttonDenyClassName,
        buttonDenyContent = "Avbryt",
        buttonDenyIconSize,
        buttonDenyIconSrc,
        buttonDenyIntent,
        buttonDenyStyle,
        title = "Vennligst bekreft",
        iconSrc = "info" as IconProps["src"],
        timeout = null,
        intent = "danger",
        isBackdropEnabled = true,
        isAutoCloseEnabled = true,
        placement = "center-center",
        onDismiss,
        content,
        ...notificationOptions
      } = props

      const id = randomId()
      const clear = () => clearNotification(id)
      const ariaLabel = notificationOptions?.["aria-label"] || "Vennligst bekreft"

      const notification: NotificationOptions = {
        ...notificationOptions,
          id,
          title,
          iconSrc,
          timeout,
          intent,
          isBackdropEnabled,
          placement,
          "aria-label": ariaLabel,
          content: (
            <Confirmation
              onConfirm={() => {
                if(onConfirm) onConfirm()
                if(isAutoCloseEnabled) clear()
              }}
              buttonConfirmClassName={buttonConfirmClassName}
              buttonConfirmContent={buttonConfirmContent}
              buttonConfirmIconSize={buttonConfirmIconSize}
              buttonConfirmIconSrc={buttonConfirmIconSrc}
              buttonConfirmIntent={buttonConfirmIntent}
              buttonConfirmStyle={buttonConfirmStyle}
              buttonConfirmAriaLabel={buttonConfirmAriaLabel}
              onDeny={() => {
                if(onDeny) onDeny()
                if(onDismiss) onDismiss()
                if(isAutoCloseEnabled) clear()
              }}
              buttonDenyClassName={buttonDenyClassName}
              buttonDenyContent={buttonDenyContent}
              buttonDenyIconSize={buttonDenyIconSize}
              buttonDenyIconSrc={buttonDenyIconSrc}
              buttonDenyIntent={buttonDenyIntent}
              buttonDenyStyle={buttonDenyStyle}
            >
              {content}
            </Confirmation>
          )
      }

      addNotification(notification)

      return clear
    }
  }
}
